import React from "react";
import styled from "styled-components";

import Fieldset from "./styled/fieldset";
import Legend from "./styled/legend";
import HeadingCell from "./styled/heading_cell";

import {EnhancedLlmRun} from "common/flowmaster/types/llm_run";
import AddIcon from "@material-ui/icons/Add";
import {IconButton} from "@material-ui/core";

import {DataKey} from "common/flowmaster/types/workflow";
import {GroupedResultEntry, GroupedResults} from "common/flowmaster/types/test";
import TestResults from "./test_editor/test_results";
import {OrganisationId} from "common/types/organisation";
import {OutputValue} from "common_components/flowmaster/llm_run/output_cell";

const OutputRow = styled.tr`
  & th,
  & td {
    padding: 0.25em 1em;
  }
  & th:not(:last-child),
  & td:not(:last-child) {
    border-right: 1px solid #ddd;
  }
`;

const Cell = styled.td`
  :not(:last-child) {
    border-right: 1px solid #ddd;
    padding-right: 1em;
  }
`;

const CellValue = styled.div`
  max-height: 10em;
  overflow: auto;
`;

const OutputCellKey = styled.td``;

const AddTests = props => (
  <IconButton
    {...props}
    size="small"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <AddIcon />
  </IconButton>
);

interface Props {
  llmRun: EnhancedLlmRun;
  organisationId: OrganisationId;
  dataKey: DataKey;
  title: string;
  isDeveloperMode: boolean;
  groupedResults: GroupedResults<GroupedResultEntry>;
  addTest: (dataKey: DataKey, key: string) => void;
  showResults: (dataKey: DataKey, key: string) => void;
}

export default function Output({
  llmRun,
  dataKey,
  title,
  addTest,
  isDeveloperMode,
  groupedResults,
  organisationId,
  showResults,
}: Props) {
  const data = llmRun.data.data_store[dataKey];
  if (!data) {
    return null;
  }

  return (
    <Fieldset>
      <Legend>{title}</Legend>
      <table style={{tableLayout: "fixed", width: "100%"}}>
        <thead>
          <OutputRow>
            <HeadingCell style={{width: "15em"}}>key</HeadingCell>
            <HeadingCell style={{width: "100%"}}>value</HeadingCell>
          </OutputRow>
        </thead>
        <tbody>
          {Object.entries(data).map(([key, item]) => {
            const testResults = groupedResults[dataKey]?.[key] ?? [];
            const thisRunResult = testResults.find(
              result => result.llm_run_id === llmRun.id,
            );

            return (
              <OutputRow key={key}>
                <OutputCellKey>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5em",
                    }}
                  >
                    <div>{key}</div>
                    {isDeveloperMode && (
                      <>
                        <div className="tests" style={{display: "flex"}}>
                          {testResults.length > 0 && (
                            <TestResults
                              results={testResults}
                              llmRunId={llmRun.id}
                              organisationId={organisationId}
                              showResults={() => showResults(dataKey, key)}
                            />
                          )}
                          <AddTests onClick={() => addTest(dataKey, key)} />
                        </div>
                        <div style={{color: "grey"}}>
                          {thisRunResult?.state === "fail" &&
                            thisRunResult.detail.map(({message}, idx) => {
                              return <div key={idx}>{message}</div>;
                            })}
                        </div>
                      </>
                    )}
                  </div>
                </OutputCellKey>
                <Cell>
                  <CellValue>
                    <OutputValue value={item} />
                  </CellValue>
                </Cell>
              </OutputRow>
            );
          })}
        </tbody>
      </table>
    </Fieldset>
  );
}
